/**
 * UnifiedDataManager - Single Source of Truth for Character and Monster Data
 * date_of_change: 2025-08-29
 * 
 * This service replaces the fragmented data layer with one unified system that:
 * 1. Uses the existing JSON schema as the ONLY data format
 * 2. Handles all JSON file I/O operations (load/save/overwrite)  
 * 3. Provides consistent CRUD operations for both monsters and characters
 * 4. Eliminates normalizers/converters by storing data correctly from the start
 * 
 * SIMPLE ARCHITECTURE:
 * JSON Files → UnifiedDataManager → AssetBrowser → BattleRoom → PropertiesPopup
 * 
 * NO MORE:
 * - Multiple data sources (templates, normalizers, converters)
 * - Schema mismatches (hpObj vs hp vs hitPoints)
 * - Duplicate CRUD paths (DataService vs CustomAssetManager)
 * 
 * SCHEMA COMPLIANCE:
 * - Monsters: Use existing monster_template.json schema
 * - Characters: Use existing master_character_schema.json schema
 * - All data stored in schema.data format with metadata wrapper
 */

import apiFetch from '../utils/apiFetch.js';
import backendUrlResolver from '../utils/BackendUrlResolver.js';
import { validateEntity as validateWithSchema, formatValidationErrors } from '../utils/EntityValidator.js';
import { DEBUG_ENABLED } from '../utils/DebugLogger';
import { assertCustomEntityCreationAllowed } from './EntitlementService';
import {
  resolveCombatantProficiencyBonus,
  resolveCombatantCurrentHp,
  resolveCombatantSpeedFeet,
  resolvePreferredAbilityScores,
  resolvePreferredArmorClass,
  resolvePreferredInitiative
} from '../utils/combat/CombatantNormalizer';

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

const BACKEND_ENTITY_LOAD_TIMEOUT_MS = 8000;

class UnifiedDataManager {
  constructor() {
    // Use dynamic backend URL resolver instead of hardcoded URLs
    // date_of_change: 2025-09-21 - Dynamic ngrok URL detection
    this.apiUrl = null; // Will be resolved dynamically
    this.storageKey = 'ascendant_unified_entities';
    this.cache = new Map();
    this.schemaVersions = {
      monster: '1.0',
      character: '1.0'
    };

    // date_of_change: 2025-08-29
    // Initialize schemas by reading the actual schema files
    this.schemas = {
      monster: null, // Will be loaded from monster_template.json
      character: null // Will be loaded from master_character_schema.json  
    };
    
    // Initialization state tracking to prevent race conditions
    this.initializationState = {
      initialized: false,
      initializing: false,
      schemasLoaded: false,
      migrationComplete: false,
      error: null
    };
    
    // Initialize asynchronously
    this.initPromise = null;
    this.startInitialization();
  }

  debugLog(...args) {
    if (DEBUG_ENABLED) {
      console.debug(...args);
    }
  }

  /**
   * Start initialization process with proper state tracking
   * date_of_change: 2025-08-29
   */
  startInitialization() {
    if (this.initializationState.initializing || this.initializationState.initialized) {
      debugLog('[UnifiedDataManager] Initialization already in progress or complete');
      return this.initPromise;
    }
    
    this.initializationState.initializing = true;
    this.initPromise = this.initializeStorage();
    return this.initPromise;
  }

  /**
   * Initialize storage and load schemas
   * date_of_change: 2025-08-29
   */
  async initializeStorage() {
    try {
      this.debugLog('[UnifiedDataManager] Starting initialization...');
      
      // Step 1: Ensure localStorage has proper structure
      const existing = localStorage.getItem(this.storageKey);
      if (!existing) {
        debugLog('[UnifiedDataManager] Creating initial localStorage structure');
        localStorage.setItem(this.storageKey, JSON.stringify({
          characters: {},
          monsters: {},
          version: '1.0'
        }));
      } else {
        this.debugLog('[UnifiedDataManager] Found existing localStorage data');
      }

      // Step 2: Load and cache schemas for validation
      await this.loadSchemas();
      this.initializationState.schemasLoaded = true;
      this.debugLog('[UnifiedDataManager] Schemas loaded');
      
      // Step 3: Auto-populate with existing data if empty
      await this.ensureDefaultData();
      this.initializationState.migrationComplete = true;
      this.debugLog('[UnifiedDataManager] Default data ensured');
      
      // Step 4: Mark as fully initialized
      this.initializationState.initialized = true;
      this.initializationState.initializing = false;
      this.debugLog('[UnifiedDataManager] Initialized successfully');
      
      return true;
    } catch (error) {
      debugLog('[UnifiedDataManager] Initialization failed:', error);
      this.initializationState.error = error;
      this.initializationState.initializing = false;
      this.initializationState.initialized = false;
      throw error; // Re-throw so we know something went wrong
    }
  }

  /**
   * Ensure we have basic default data available
   * date_of_change: 2025-08-29
   */
  async ensureDefaultData() {
    try {
      this.debugLog('[UnifiedDataManager] Checking for existing data...');
      
      // Check localStorage directly to avoid circular dependency during initialization
      const localData = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      const monsters = localData.monsters || {};
      const characters = localData.characters || {};

      this.debugLog('[UnifiedDataManager] Current data counts:', {
        monsters: Object.keys(monsters).length,
        characters: Object.keys(characters).length
      });

      // If we're missing any data, trigger full migration
      if (Object.keys(monsters).length === 0 || Object.keys(characters).length === 0) {
        debugLog('[UnifiedDataManager] Missing data detected, running migration...');
        
        try {
          // Dynamic import to avoid circular dependency
          const DataMigration = await import('./DataMigration.js');
          const migrationResult = await DataMigration.default.migrateAllData();
          
          debugLog('[UnifiedDataManager] Migration completed:', migrationResult);
        } catch (migrationError) {
          debugLog('[UnifiedDataManager] Migration failed:', migrationError);
          debugLog('[UnifiedDataManager] Migration error details:', migrationError.stack);
          // Don't throw - we can still function without migration
        }
      } else {
        this.debugLog('[UnifiedDataManager] Found existing data, skipping full migration');
        try {
          const DataMigration = await import('./DataMigration.js');
          await Promise.all([
            DataMigration.default.migratePreloadedCharacters(),
            DataMigration.default.migratePreloadedMonsters()
          ]);
          this.debugLog('[UnifiedDataManager] Reconciled built-in templates (characters and monsters)');
        } catch (migrationError) {
          debugLog('[UnifiedDataManager] Template reconciliation failed:', migrationError.message);
        }
      }

    } catch (error) {
      debugLog('[UnifiedDataManager] Failed to populate default data:', error);
      debugLog('[UnifiedDataManager] Error details:', error.stack);
    }
  }

  /**
   * Load JSON schemas for validation
   * date_of_change: 2025-08-29
   */
  async loadSchemas() {
    try {
      // Since schemas are outside src/, we'll use basic validation instead
      // This is acceptable since we control the schema format
      this.debugLog('[UnifiedDataManager] Using built-in schema validation');
      this.schemas.monster = { loaded: true };
      this.schemas.character = { loaded: true };
    } catch (error) {
      debugLog('[UnifiedDataManager] Schema setup failed:', error);
    }
  }

  /**
   * Create new entity from template with proper schema structure
   * date_of_change: 2025-08-29
   * 
   * @param {string} type - 'character' or 'monster'  
   * @param {string} templateId - Template identifier
   * @param {Object} customizations - Additional properties to override
   * @returns {Object} - Complete entity in schema format
   */
  createEntity(type, templateId, customizations = {}) {
    const now = new Date().toISOString();
    const id = customizations.id || this.generateId(type, customizations.name || templateId);

    if (type === 'monster') {
      return {
        schemaVersion: this.schemaVersions.monster,
        type: 'monster',
        exportDate: now,
        templateId: templateId,
        id: id,
        source: {
          name: 'Virtual Combat Simulator Custom',
          edition: '5e', 
          license: 'Custom'
        },
        data: {
          name: customizations.name || 'New Monster',
          size: customizations.size || 'Medium',
          creatureType: {
            type: customizations.creatureType || 'Humanoid',
            tags: customizations.tags || []
          },
          alignment: customizations.alignment || 'neutral',
          cr: {
            display: customizations.cr || '1/4',
            value: this.crToNumber(customizations.cr || '1/4'),
            xp: this.crToXP(customizations.cr || '1/4'),
            pb: this.crToPB(customizations.cr || '1/4')
          },
          ac: {
            value: customizations.ac || 10,
            notes: customizations.acNotes || ''
          },
          hp: {
            average: customizations.hp || 10,
            formula: customizations.hpFormula || '2d8+2'
          },
          speed: {
            walk: customizations.speed || 30,
            ...customizations.additionalSpeeds
          },
          abilities: {
            str: customizations.str || 10,
            dex: customizations.dex || 10, 
            con: customizations.con || 10,
            int: customizations.int || 10,
            wis: customizations.wis || 10,
            cha: customizations.cha || 10,
            ...customizations.abilities
          },
          initiative: customizations.initiative || Math.floor(((customizations.dex || 10) - 10) / 2),
          skills: customizations.skills || {},
          senses: {
            passivePerception: customizations.passivePerception || 10,
            ...customizations.senses
          },
          languages: {
            list: customizations.languages || ['Common']
          },
          traits: customizations.traits || [],
          actions: customizations.actions || [],
          environments: customizations.environments || []
        },
        tags: ['custom'],
        // Include non-schema metadata for internal use
        _meta: {
          createdAt: now,
          lastModified: now,
          artwork: customizations.artwork || null
        }
      };
    }

    if (type === 'character') {
      return {
        schemaVersion: this.schemaVersions.character,
        type: 'character',
        exportDate: now,
        id: id,
        source: {
          name: 'Virtual Combat Simulator Custom',
          edition: '5e',
          license: 'Custom'
        },
        data: {
          identity: {
            name: customizations.name || 'New Character',
            race: customizations.race || 'Human',
            background: customizations.background || 'Adventurer',
            alignment: customizations.alignment || 'neutral'
          },
          level: customizations.level || 1,
          classes: [{
            name: customizations.characterClass || 'Fighter', 
            level: customizations.level || 1,
            subclass: customizations.subclass || '',
            hitDie: customizations.hitDie || 'd10'
          }],
          abilities: {
            str: customizations.str || 13,
            dex: customizations.dex || 12,
            con: customizations.con || 14,
            int: customizations.int || 10,
            wis: customizations.wis || 12,
            cha: customizations.cha || 8,
            ...customizations.abilities
          },
          proficiencyBonus: Math.ceil((customizations.level || 1) / 4) + 1,
          hp: {
            current: customizations.hp || 10,
            maximum: customizations.maxHp || customizations.hp || 10,
            temporary: 0
          },
          ac: {
            value: customizations.ac || 10,
            source: customizations.acSource || 'base'
          },
          speed: {
            walk: customizations.speed || 30,
            ...customizations.additionalSpeeds
          },
          saves: customizations.saves || {},
          skills: customizations.skills || {},
          features: customizations.features || [],
          inventory: customizations.inventory || {
            coins: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },
            equipment: []
          }
        },
        // Include non-schema metadata for internal use
        _meta: {
          createdAt: now,
          lastModified: now,
          artwork: customizations.artwork || null
        }
      };
    }

    throw new Error(`Invalid entity type: ${type}`);
  }

  /**
   * Load entity by ID from storage
   * date_of_change: 2025-08-29
   * 
   * @param {string} id - Entity ID
   * @param {string} type - Optional type hint for faster lookup
   * @returns {Object|null} - Entity data or null if not found
   */
  async loadEntity(id, type = null) {
    try {
      // Check cache first
      if (this.cache.has(id)) {
        debugLog(`[UnifiedDataManager] Cache hit for ${id}`);
        return this.cache.get(id);
      }

      // Try backend first
      const entity = await this.loadFromBackend(id, type);
      if (entity) {
        this.cache.set(id, entity);
        return entity;
      }

      // Fallback to localStorage
      const localEntity = this.loadFromLocalStorage(id, type);
      if (localEntity) {
        this.cache.set(id, localEntity);
        return localEntity;  
      }

      // Try loading from existing data files for backwards compatibility
      if (type === 'monster') {
        const fileEntity = await this.loadFromDataFiles(id);
        if (fileEntity) {
          this.cache.set(id, fileEntity);
          return fileEntity;
        }
      }

      debugLog(`[UnifiedDataManager] Entity not found: ${id}`);
      return null;

    } catch (error) {
      debugLog(`[UnifiedDataManager] Failed to load entity ${id}:`, error);
      return null;
    }
  }

  /**
   * Compatibility alias for older UI call sites.
   * date_of_change: 2026-03-29
   *
   * @param {string} id - Entity ID
   * @param {string} type - Optional type hint
   * @returns {Promise<Object|null>} - Entity data or null if not found
   */
  async getEntityById(id, type = null) {
    return this.loadEntity(id, type);
  }

  /**
   * Save entity with proper schema validation and backend sync
   * date_of_change: 2025-08-29
   * 
   * @param {Object} entity - Complete entity object in schema format
   * @param {boolean} overwrite - Allow overwriting existing entities
   * @returns {Object} - Saved entity with updated metadata
   */
  async saveEntity(entity, overwrite = true) {
    try {
      debugLog(`[UnifiedDataManager] Saving ${entity.type}: ${entity.data?.name || entity.data?.identity?.name}`);

      // Validate entity against schema
      if (!this.validateEntity(entity)) {
        throw new Error(`Entity validation failed for ${entity.id}`);
      }

      // date_of_change: 2026-04-03
      // purpose: Tier enforcement for user-created content before any persistence path runs.
      const isTemplate = (
        (Array.isArray(entity.tags) && entity.tags.includes('template')) ||
        (Array.isArray(entity?.metadata?.tags) && entity.metadata.tags.includes('template')) ||
        entity?.source?.name === 'Virtual Combat Simulator Templates'
      );

      if (!isTemplate && (entity.type === 'character' || entity.type === 'monster')) {
        await assertCustomEntityCreationAllowed({
          entityType: entity.type,
          entityId: entity.id
        });
      }

      // Update metadata
      entity._meta = {
        ...entity._meta,
        lastModified: new Date().toISOString(),
        createdAt: entity._meta?.createdAt || new Date().toISOString()
      };

      // date_of_change: 2025-11-30 - AGENTS.md compliance: do not push built-in templates to backend
      // Built-in templates are local-only. Detect via either top-level tags, metadata.tags, or known source name.
      let savedEntity;
      if (isTemplate) {
        this.debugLog('[UnifiedDataManager] Skipping backend for template entity; saving locally only');
        savedEntity = this.saveToLocalStorage(entity);
      } else {
        // Try to save to backend first
        try {
          savedEntity = await this.saveToBackend(entity);
          debugLog(`[UnifiedDataManager] Saved to backend: ${entity.id}`);
        } catch (backendError) {
          // AGENTS.md: No silent fallbacks in production.
          const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
          const isProdHost = !!host && (host === 'virtual-combat-simulator-x1tf.vercel.app' || host.endsWith('vercel.app') || host.endsWith('netlify.app') || host.endsWith('herokuapp.com') || (typeof window !== 'undefined' && window.location?.protocol === 'https:'));
          if (isProdHost) {
            debugLog('[UnifiedDataManager] Backend save failed in production; refusing local fallback by policy:', backendError?.message || backendError);
            throw backendError;
          }
          debugLog('[UnifiedDataManager] Backend save failed (non-production), using localStorage:', backendError?.message || backendError);
          savedEntity = this.saveToLocalStorage(entity);
        }
      }

      // Update cache
      this.cache.set(entity.id, savedEntity);

      this.debugLog(`[UnifiedDataManager] Successfully saved: ${entity.id}`);
      return savedEntity;

    } catch (error) {
      debugLog('[UnifiedDataManager] Save failed:', error);
      throw error;
    }
  }

  /**
   * Delete entity from all storage locations
   * date_of_change: 2025-08-29
   * 
   * @param {string} id - Entity ID to delete
   * @param {string} type - Entity type for targeted deletion
   * @returns {boolean} - Success status
   */
  async deleteEntity(id, type = null) {
    try {
      debugLog(`[UnifiedDataManager] Deleting entity: ${id}`);

      // Delete from backend
      try {
        await this.deleteFromBackend(id, type);
      } catch (error) {
        debugLog('[UnifiedDataManager] Backend delete failed:', error);
      }

      // Delete from localStorage
      this.deleteFromLocalStorage(id, type);

      // Clear cache
      this.cache.delete(id);

      debugLog(`[UnifiedDataManager] Successfully deleted: ${id}`);
      return true;

    } catch (error) {
      debugLog(`[UnifiedDataManager] Delete failed for ${id}:`, error);
      return false;
    }
  }

  /**
   * Get all entities of a specific type
   * date_of_change: 2025-08-29
   * 
   * @param {string} type - 'character' or 'monster'
   * @returns {Object} - Object with entity IDs as keys and entities as values
   */
  async getAllEntities(type) {
    try {
      debugLog(`[UnifiedDataManager] Getting all ${type}s...`);
      
      // Ensure initialization is complete with timeout protection
      if (!this.initializationState.initialized) {
        debugLog('[UnifiedDataManager] Waiting for initialization...');
        debugLog('[UnifiedDataManager] Current state:', this.initializationState);
        
        if (this.initializationState.error) {
          throw new Error(`Initialization failed: ${this.initializationState.error.message}`);
        }
        
        if (!this.initPromise) {
          this.debugLog('[UnifiedDataManager] No init promise, starting initialization...');
          this.startInitialization();
        }
        
        // Wait for initialization with timeout
        const initResult = await Promise.race([
          this.initPromise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Initialization timeout')), 10000)
          )
        ]);
        
        debugLog('[UnifiedDataManager] Initialization completed:', initResult);
      }
      
      // Try backend first, but don't let it break the entire process
      let backendEntities = {};
      try {
        // A slow backend must not leave the BattleRoom token library in an
        // endless spinner when cached/default entities are already available.
        // Bound the remote phase, then continue with local sources.
        // date_of_change: 2026-08-18
        backendEntities = await Promise.race([
          this.getAllFromBackend(type),
          new Promise((_, reject) => setTimeout(
            () => reject(new Error('Backend entity load timeout')),
            BACKEND_ENTITY_LOAD_TIMEOUT_MS
          ))
        ]);
        debugLog(`[UnifiedDataManager] Backend ${type}s:`, Object.keys(backendEntities).length);
      } catch (backendError) {
        debugLog(`[UnifiedDataManager] Backend failed, using localStorage fallback:`, backendError.message);
      }

      // Always include localStorage
      const localEntities = this.getAllFromLocalStorage(type);
      debugLog(`[UnifiedDataManager] Local ${type}s:`, Object.keys(localEntities).length);

      // Merge local + backend, prefer backend on collisions
      const mergedEntities = { ...localEntities, ...backendEntities };

      // Also include custom assets from CustomAssetManager for compatibility
      // date_of_change: 2025-11-30 - FIX: CustomAssetManager default export is a singleton instance, not a class
      try {
        const { default: customManager } = await import('./CustomAssetManager.js');
        const customAssets = customManager.getAssetsByType(type) || [];
        debugLog(`[UnifiedDataManager] Custom ${type}s from CustomAssetManager:`, customAssets.length);

        // Convert custom assets to unified format and merge
        for (const customAsset of customAssets) {
          const baseId = customAsset.id || (customAsset.name ? customAsset.name.toLowerCase().replace(/[^a-z0-9]+/g, '_') : 'asset');
          const entityId = baseId.startsWith('custom_') ? baseId : `custom_${baseId}`;
          if (!mergedEntities[entityId]) {
            mergedEntities[entityId] = this.convertCustomAssetToUnifiedEntity(customAsset, type, entityId);
            debugLog(`[UnifiedDataManager] Added custom ${type}: ${customAsset.name || entityId}`);
          }
        }
      } catch (customError) {
        debugLog(`[UnifiedDataManager] Failed to load custom ${type}s:`, customError.message);
      }
      
      debugLog(`[UnifiedDataManager] Total ${type}s (backend + local + custom):`, Object.keys(mergedEntities).length);
      return mergedEntities;

    } catch (error) {
      debugLog(`[UnifiedDataManager] Failed to get all ${type}s:`, error);
      return {};
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Generate unique ID for entities
   * date_of_change: 2025-08-29
   */
  generateId(type, name = '') {
    const sanitized = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 6);
    return `${type}_${sanitized}_${timestamp}_${random}`;
  }

  /**
   * Convert CR string to numeric value
   * date_of_change: 2025-08-29
   */
  crToNumber(crString) {
    if (!crString || crString === '0') return 0;
    if (crString.includes('/')) {
      const [num, den] = crString.split('/');
      return parseInt(num) / parseInt(den);
    }
    return parseFloat(crString);
  }

  /**
   * Convert CR to XP value
   * date_of_change: 2025-08-29
   */
  crToXP(crString) {
    const crValue = this.crToNumber(crString);
    const xpTable = {
      0: 10, 0.125: 25, 0.25: 50, 0.5: 100, 1: 200, 2: 450, 3: 700,
      4: 1100, 5: 1800, 6: 2300, 7: 2900, 8: 3900, 9: 5000, 10: 5900,
      11: 7200, 12: 8400, 13: 10000, 14: 11500, 15: 13000, 16: 15000,
      17: 18000, 18: 20000, 19: 22000, 20: 25000, 21: 33000, 22: 41000,
      23: 50000, 24: 62000, 25: 75000, 26: 90000, 27: 105000, 28: 120000,
      29: 135000, 30: 155000
    };
    return xpTable[crValue] || 0;
  }

  /**
   * Convert CR to proficiency bonus
   * date_of_change: 2025-08-29
   */
  crToPB(crString) {
    const crValue = this.crToNumber(crString);
    if (crValue <= 4) return 2;
    if (crValue <= 8) return 3;
    if (crValue <= 12) return 4;
    if (crValue <= 16) return 5;
    if (crValue <= 20) return 6;
    if (crValue <= 24) return 7;
    if (crValue <= 28) return 8;
    return 9;
  }

  // ==================== BACKEND OPERATIONS ====================

  async loadFromBackend(id, type) {
    const endpoint = type
      ? `/api/${type}s/${encodeURIComponent(id)}`
      : `/api/entities/${encodeURIComponent(id)}`;

    const response = await apiFetch(endpoint);
    if (response.ok) {
      const result = await response.json();
      return result.data;
    }
    return null;
  }

  async saveToBackend(entity) {
    const endpoint = `/api/${entity.type}s`;
    const response = await apiFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entity),
      requireAuth: true
    });

    if (!response.ok) {
      throw new Error(`Backend save failed: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  async deleteFromBackend(id, type) {
    const entityType = type || 'entities';
    const endpoint = `/api/${entityType}/${encodeURIComponent(id)}`;

    const response = await apiFetch(endpoint, { method: 'DELETE', requireAuth: true });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Backend delete failed: ${response.statusText}`);
    }
  }

  async getAllFromBackend(type) {
    try {
      const backendUrl = await backendUrlResolver.getBackendUrl();
      const pageSize = 100;
      const maxPages = 50;
      const merged = {};
      const seenIds = new Set();

      for (let page = 1; page <= maxPages; page++) {
        const offset = (page - 1) * pageSize;
        const candidates = [
          `/api/${type}s?limit=${pageSize}&page=${page}`,
          `/api/${type}s?page=${page}&perPage=${pageSize}`,
          `/api/${type}s?page=${page}&pageSize=${pageSize}`,
          `/api/${type}s?limit=${pageSize}&offset=${offset}`,
          `/api/${type}s?skip=${offset}&limit=${pageSize}`
        ];

        let pageList = null;
        let lastResponse = null;

        for (const endpoint of candidates) {
          try {
            debugLog(`[UnifiedDataManager] Fetching ${type}s from backend: ${backendUrl}${endpoint}`);
            const response = await apiFetch(endpoint);
            lastResponse = response;
            if (!response.ok) continue;
            const result = await response.json();
            const list = Array.isArray(result)
              ? result
              : (result.data || result.items || result.results || []);
            if (Array.isArray(list)) {
              pageList = list;
              break;
            }
          } catch (_) {}
        }

        if (!pageList) {
          if (lastResponse) {
            debugLog(`[UnifiedDataManager] Backend response not ok for page ${page}: ${lastResponse.status} ${lastResponse.statusText}`);
          } else {
            debugLog(`[UnifiedDataManager] No usable backend response for page ${page}`);
          }
          break;
        }

        const before = seenIds.size;
        for (const entity of pageList) {
          if (!entity || !entity.id) continue;
          if (!seenIds.has(entity.id)) {
            seenIds.add(entity.id);
            merged[entity.id] = entity;
          }
        }

        this.debugLog(`[UnifiedDataManager] Fetched page ${page} with ${pageList.length} ${type}s (accumulated: ${Object.keys(merged).length})`);

        if (pageList.length === 0) break;
        if (seenIds.size === before) break;
      }

      this.debugLog(`[UnifiedDataManager] Backend returned total ${Object.keys(merged).length} ${type}s`);
      return merged;
    } catch (error) {
      debugLog(`[UnifiedDataManager] Backend request failed:`, error.message);
      return {};
    }
  }

  // ==================== LOCAL STORAGE OPERATIONS ====================

  loadFromLocalStorage(id, type) {
    const data = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
    
    if (type && data[type + 's']?.[id]) {
      return data[type + 's'][id];
    }

    // Search all types if type not specified
    for (const entityType of ['characters', 'monsters']) {
      if (data[entityType]?.[id]) {
        return data[entityType][id];
      }
    }
    
    return null;
  }

  saveToLocalStorage(entity) {
    const data = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
    const entityType = entity.type + 's';
    
    if (!data[entityType]) data[entityType] = {};
    
    data[entityType][entity.id] = entity;
    localStorage.setItem(this.storageKey, JSON.stringify(data));
    
    return entity;
  }

  deleteFromLocalStorage(id, type) {
    const data = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
    
    if (type && data[type + 's']) {
      delete data[type + 's'][id];
    } else {
      // Delete from all types if type not specified
      for (const entityType of ['characters', 'monsters']) {
        if (data[entityType]?.[id]) {
          delete data[entityType][id];
        }
      }
    }
    
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  getAllFromLocalStorage(type) {
    const data = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
    const entities = data[type + 's'] || {};
    this.debugLog(`[UnifiedDataManager] LocalStorage has ${Object.keys(entities).length} ${type}s:`, Object.keys(entities));
    this.debugLog(`[UnifiedDataManager] LocalStorage structure:`, data);
    return entities;
  }

  /**
   * Convert a CustomAssetManager asset into a unified entity shape and preserve artwork keys
   * date_of_change: 2025-11-12
   * rule_reference: AGENTS.md SSOT, no fallbacks — ensure resolver receives canonical upload keys
   */
  convertCustomAssetToUnifiedEntity(customAsset, type, forcedId = null) {
    try {
      const now = new Date().toISOString();

      const pick = (...vals) => vals.find(v => v != null && String(v).trim().length > 0);

      // Prefer canonicalData from backend/imports; fall back to customData or raw
      const rawData = customAsset?.canonicalData || customAsset?.customData || customAsset?.data || customAsset || {};

      // Normalize ID with 'custom_' prefix for consistency with callers
      const baseId = forcedId || customAsset?.id || rawData?.id || (customAsset?.name ? customAsset.name.toLowerCase().replace(/[^a-z0-9]+/g, '_') : `asset_${Date.now()}`);
      const id = String(baseId).startsWith('custom_') ? baseId : `custom_${baseId}`;

      // Extract any artwork/token keys (uploads/* preferred)
      const rawArt = pick(
        customAsset?.tokenAssetKey,
        customAsset?.tokenAsset?.key,
        customAsset?.artwork,
        customAsset?.imageUrl,
        rawData?.tokenAssetKey,
        rawData?.tokenAsset?.key,
        rawData?.artwork,
        rawData?.imageUrl
      );

      const normalizeUploadKey = (val) => {
        if (!val || typeof val !== 'string') return null;
        const s = val.trim();
        if (!s) return null;
        if (s.startsWith('/uploads/')) return s.slice(1);
        if (s.startsWith('uploads/')) return s;
        const idx = s.indexOf('/uploads/');
        if (idx !== -1) return s.slice(idx + 1); // drop leading '/'
        return null; // not an upload path; allow resolver to handle absolute URLs via hints
      };

      const tokenKey = normalizeUploadKey(rawArt);
      const metaArtwork = tokenKey ? `/${tokenKey}` : (rawData?.artwork || customAsset?.artwork || null);

      // Ensure minimal required display identity where possible
      if (type === 'character') {
        if (!rawData.identity) rawData.identity = {};
        if (!rawData.identity.name) rawData.identity.name = customAsset?.name || rawData?.name || id;
      } else if (type === 'monster') {
        if (!rawData.name) rawData.name = customAsset?.name || id;
      }

      return {
        schemaVersion: this.schemaVersions[type] || '1.0',
        type,
        exportDate: now,
        id,
        source: { name: 'Custom Asset', edition: '5e', license: 'Custom' },
        data: rawData,
        tags: ['custom'],
        _meta: {
          createdAt: customAsset?.createdAt || now,
          lastModified: customAsset?.lastModified || now,
          artwork: metaArtwork || null,
          ...(tokenKey ? { tokenAssetKey: tokenKey, tokenAsset: { key: tokenKey } } : {})
        }
      };
    } catch (e) {
      debugLog('[UnifiedDataManager] Failed to convert custom asset to unified entity:', e?.message || e);
      // Fallback minimal wrapper
      return {
        schemaVersion: this.schemaVersions[type] || '1.0',
        type,
        id: forcedId || (customAsset?.id || `custom_${Date.now()}`),
        data: customAsset?.canonicalData || customAsset?.customData || customAsset || {},
        tags: ['custom'],
        _meta: { createdAt: new Date().toISOString(), lastModified: new Date().toISOString() }
      };
    }
  }

  /**
   * Load legacy SRD monsters directly from data files and adapt them into canonical entities.
   * date_of_change: 2025-12-03
   * rule_reference: AGENTS.md SSOT — even file-based fallbacks must emit monster_template.json entities.
   */
  async loadFromDataFiles(monsterId) {
    try {
      // Known monster files for backwards compatibility
      const monsterFiles = [
        'goblin', 'skeleton', 'zombie', 'wolf', 'dire-wolf',
        'brown-bear', 'bugbear', 'hobgoblin', 'orc', 'owlbear'
      ];

      if (monsterFiles.includes(monsterId)) {
        const response = await fetch(`/src/data/monsters/${monsterId}.json`);
        if (response.ok) {
          const raw = await response.json();

          try {
            const { buildMonsterEntityFromRaw } = await import('../normalizers/MonsterEntityAdapter.js');
            const now = new Date().toISOString();
            const { id, data, metadata } = buildMonsterEntityFromRaw(raw, {
              now,
              defaultId: monsterId,
              markAsTemplate: true,
              markAsUpload: false
            });

            return {
              schemaVersion: this.schemaVersions.monster,
              type: 'monster',
              id,
              data,
              metadata
            };
          } catch (adaptError) {
            debugLog('[UnifiedDataManager] Failed to adapt legacy monster JSON via MonsterEntityAdapter, returning raw file entity:', adaptError);
            return raw;
          }
        }
      }

      return null;
    } catch (error) {
      debugLog(`[UnifiedDataManager] Failed to load from data files: ${monsterId}`, error);
      return null;
    }
  }

  /**
   * Validate entity against loaded JSON schema
   * date_of_change: 2025-12-04
   * rule_reference: AGENTS.md STRICT VALIDATION GATES — keep strict data.*, tolerate root drift
   */
  validateEntity(entity) {
    if (!entity || typeof entity !== 'object') {
      debugLog('[UnifiedDataManager] Entity missing or not an object');
      return false;
    }

    // Validate minimal wrapper for monsters to avoid root-level additionalProperties on older schemas
    const validationEntity = (entity.type === 'monster')
      ? { type: 'monster', data: entity.data }
      : entity;

    const { ok, errors, entityType } = validateWithSchema(validationEntity);

    if (!ok) {
      const formattedErrors = formatValidationErrors(errors);
      const errorDetails = (errors || []).map(e => ({
        keyword: e.keyword,
        instancePath: e.instancePath || e.dataPath || '',
        params: e.params,
        message: e.message
      }));
      const snapshot = {
        rootKeys: Object.keys(entity || {}),
        validatedRootKeys: Object.keys(validationEntity || {}),
        dataKeys: entity && entity.data && typeof entity.data === 'object' ? Object.keys(entity.data) : []
      };
      debugLog('[UnifiedDataManager] Schema validation failed', {
        entityId: entity.id,
        entityType,
        errors: errorDetails,
        formattedErrors,
        snapshot
      });

      throw new Error(
        `[UnifiedDataManager] Schema validation failed for ${entity.id || 'unknown entity'} (${entityType}). ` +
        `${formattedErrors || 'See console for full error list.'}`
      );
    }

    return true;
  }

  /**
   * Convenience method to get display-ready data for UI components
   * date_of_change: 2025-08-29
   */
  getDisplayData(entity) {

    if (!entity || typeof entity !== 'object') return {};

    if (entity.type === 'monster') {
      // S3 stores both schema-wrapped monsters and flat editor-compatible GMC
      // projections. Read the canonical document directly in either case so a
      // projection is not reduced to an empty display row. date_of_change: 2026-08-01
      const d = entity.data && typeof entity.data === 'object' ? entity.data : entity;
      // date_of_change: 2025-12-01 - Canonical monster fields per monster_template.json
      const crDisplay = (d.challenge && d.challenge.cr != null)
        ? d.challenge.cr
        : ((d.cr && (d.cr.display || d.cr)) || '0');
      const maxHpVal = d.defense?.hp?.max
        ?? d.maxHp
        ?? d.hpObj?.max
        ?? d.hitPoints?.max
        ?? d.hitPoints?.maximum
        ?? d.hp?.maximum
        ?? d.hp?.max
        ?? d.hp?.average
        ?? (typeof d.hp === 'number' ? d.hp : null);
      const hpVal = d.defense?.hp?.current
        ?? d.defense?.hp?.max
        ?? resolveCombatantCurrentHp(d)
        ?? maxHpVal;
      const acVal = d.defense?.ac?.base ?? resolvePreferredArmorClass({ runtimeSources: [d], fallbackValue: null });
      const size = d.taxonomy?.size || d.size || null;
      const alignment = d.taxonomy?.alignment || d.alignment || null;
      return {
        id: entity.id,
        name: d.name,
        type: 'monster',
        cr: crDisplay,
        hp: hpVal,
        maxHp: maxHpVal,
        ac: acVal,
        size,
        alignment,
        abilities: d.abilities,
        actions: d.actions || [],
        traits: d.traits || [],
        artwork: entity._meta?.artwork || d.identity?.image || null,
        // Include full schema data for components that need it
        schemaData: d
      };
    }

    if (entity.type === 'character') {
      const d = entity.data && typeof entity.data === 'object' ? entity.data : entity;
      const name = d.identity?.name || d.name;
      const cls = (Array.isArray(d.classes) && d.classes[0]?.name) || d.class;
      const level = d.level ?? (Array.isArray(d.classes) ? d.classes[0]?.level : null);
      const race = d.identity?.race || d.race;
      const hpVal = (typeof d.hp === 'number')
        ? d.hp
        : (d.hitPoints?.current ?? d.hp?.current ?? d.hp?.value ?? null);
      const maxHpVal = (typeof d.maxHp === 'number')
        ? d.maxHp
        : (d.hitPoints?.maximum ?? d.hitPoints?.max ?? d.hp?.maximum ?? d.hp?.max ?? null);
      const acVal = resolvePreferredArmorClass({
        runtimeSources: [d],
        canonicalSources: [d.compat?.originalData],
        runtimeOverrideSource: d,
        fallbackValue: null
      });
      const abilities = resolvePreferredAbilityScores({
        runtimeSources: [d.abilities, d.stats, d],
        canonicalSources: [d.compat?.originalData],
        runtimeOverrideSource: d,
        fallbackValue: 10
      });
      const speed = resolveCombatantSpeedFeet(d, d.compat?.originalData);
      const proficiencyBonus = resolveCombatantProficiencyBonus(d, d.compat?.originalData);
      const initiative = resolvePreferredInitiative({
        runtimeSources: [d],
        canonicalSources: [d.compat?.originalData],
        runtimeOverrideSource: d,
        fallbackValue: null
      });
      return {
        id: entity.id,
        name,
        type: 'character',
        class: cls,
        level,
        race,
        hp: hpVal,
        maxHp: maxHpVal,
        ac: acVal,
        speed,
        proficiencyBonus,
        initiative,
        alignment: d.identity?.alignment || d.alignment,
        abilities,
        spells: d.spells || undefined,
        spellcasting: d.spellcasting || undefined,
        equipment: d.equipment || undefined,
        features: d.features || undefined,
        classFeatures: d.classFeatures || undefined,
        feats: d.feats || undefined,
        artwork: entity._meta?.artwork,
        // Include full schema data for components that need it
        schemaData: d
      };
    }

    return {};
  }
}

// Export singleton instance
const unifiedDataManager = new UnifiedDataManager();
export default unifiedDataManager;
